import { db, type Item } from './schema';
import { deleteImages, storeImage } from './images';

/**
 * The item's photos: the cover and the gallery behind it.
 *
 * Every write goes through `write()` below, because the invariant that keeps
 * "make cover" from eventually producing duplicates — `imageIds` never
 * contains `coverImageId` — belongs in the data layer, not in a component
 * that happens to remember it today.
 */

export type AddResult = {
  added: number;
  /** Filenames that couldn't be read. A drop of twenty holiday snaps usually
      contains one thing that isn't an image; that shouldn't lose the rest. */
  failed: string[];
};

/**
 * Appends to the gallery. Sequential rather than `Promise.all`: twenty
 * simultaneous `createImageBitmap` calls on full-size phone photos stall the
 * frame the drop landed on.
 */
export async function addGalleryImages(
  itemId: string,
  files: readonly Blob[],
): Promise<AddResult> {
  const stored: string[] = [];
  const failed: string[] = [];

  // Outside the transaction: canvas encoding is slow enough to time one out.
  for (const file of files) {
    try {
      stored.push(await storeImage(file));
    } catch {
      failed.push(file instanceof File ? file.name : 'One file');
    }
  }

  const kept = await write(itemId, (item) => ({ imageIds: [...item.imageIds, ...stored] }));
  // The item was deleted while the photos were encoding. Don't leave the
  // blobs behind with nothing pointing at them.
  if (!kept) await deleteImages(stored);

  return { added: kept ? stored.length : 0, failed };
}

/**
 * Appends images that are already stored. Link import downloads and encodes
 * everything before it touches the item, so it arrives with ids rather than
 * files — but it still has to come through the one writer, because an item
 * with photos and no cover would draw as a blank tile.
 *
 * Returns false when the item was deleted while the photos were downloading.
 */
export async function attachImages(
  itemId: string,
  imageIds: readonly string[],
): Promise<boolean> {
  if (imageIds.length === 0) return true;

  return write(itemId, (item) => ({ imageIds: [...item.imageIds, ...imageIds] }));
}

/** Drag to reorder. `to` is the index the photo should end up at. */
export async function reorderGallery(itemId: string, from: number, to: number): Promise<void> {
  await write(itemId, (item) => {
    const ids = [...item.imageIds];
    const moved = ids[from];
    if (moved === undefined) return {};

    ids.splice(from, 1);
    ids.splice(Math.max(0, Math.min(to, ids.length)), 0, moved);
    return { imageIds: ids };
  });
}

/**
 * A swap, not a promotion: the outgoing cover takes the promoted photo's
 * place in the gallery rather than being discarded.
 */
export async function makeCover(itemId: string, imageId: string): Promise<void> {
  await write(itemId, (item) => {
    const index = item.imageIds.indexOf(imageId);
    if (index === -1) return {};

    const ids = [...item.imageIds];
    if (item.coverImageId === undefined) ids.splice(index, 1);
    else ids[index] = item.coverImageId;

    return { coverImageId: imageId, imageIds: ids };
  });
}

/** Removes the photo from the item and deletes the stored image with it. */
export async function removeItemImage(itemId: string, imageId: string): Promise<void> {
  const removed = await write(itemId, (item) => ({
    // Clearing the cover doesn't leave the item without one — `write`
    // promotes the first gallery photo.
    coverImageId: item.coverImageId === imageId ? undefined : item.coverImageId,
    imageIds: item.imageIds.filter((id) => id !== imageId),
  }));

  if (removed) await deleteImages([imageId]);
}

type Change = {
  coverImageId?: string | undefined;
  imageIds?: string[];
};

/**
 * The single writer. Returns false when the item is gone.
 *
 * A key absent from `change` is left as it was; `coverImageId: undefined` is
 * a deliberate clear, which is why this reads the key's presence rather than
 * its value.
 */
async function write(itemId: string, change: (item: Item) => Change): Promise<boolean> {
  return db.transaction('rw', db.items, async () => {
    const item = await db.items.get(itemId);
    if (item === undefined) return false;

    const next = change(item);
    let coverImageId = 'coverImageId' in next ? next.coverImageId : item.coverImageId;
    let imageIds = gallery(next.imageIds ?? item.imageIds, coverImageId);

    // An item with photos always has a cover: the board has nothing to draw
    // otherwise, and removing the cover shouldn't blank the tile.
    if (coverImageId === undefined && imageIds.length > 0) {
      coverImageId = imageIds[0];
      imageIds = imageIds.slice(1);
    }

    await db.items.update(itemId, { coverImageId, imageIds });
    return true;
  });
}

/** The invariant, as a function: the gallery excludes the cover. */
function gallery(ids: readonly string[], coverImageId: string | undefined): string[] {
  return [...new Set(ids)].filter((id) => id !== coverImageId);
}
